import { Component, type ErrorInfo, type ReactNode } from "react";
import { Starfield } from "./Starfield";
import {
  hardReloadForStaleAssets,
  isChunkLoadError,
} from "../lib/staleAssets";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
  /** True once we've decided the error is a stale-asset chunk load failure. */
  isStaleAsset: boolean;
  /** True after we've exhausted the auto-reload budget. */
  reloadExhausted: boolean;
}

const INITIAL_STATE: State = {
  error: null,
  info: null,
  isStaleAsset: false,
  reloadExhausted: false,
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = INITIAL_STATE;

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, isStaleAsset: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });

    if (isChunkLoadError(error)) {
      const reloaded = hardReloadForStaleAssets();
      if (!reloaded) {
        this.setState({ reloadExhausted: true });
      }
      return;
    }

    // Surface non-recoverable errors to the console for devs / Sentry.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  private handleReload = () => {
    const reloaded = hardReloadForStaleAssets();
    if (!reloaded) {
      // Budget exhausted — do a plain reload so the user isn't stuck.
      window.location.reload();
    }
  };

  private handleGoHome = () => {
    window.location.assign("/");
  };

  render() {
    const { error, isStaleAsset, reloadExhausted } = this.state;

    if (!error) return this.props.children;

    // If the page is already navigating away (hardReloadForStaleAssets
    // triggered a replace), keep the UI quiet to avoid a flash of the
    // error screen.
    if (isStaleAsset && !reloadExhausted) {
      return <ErrorScreen variant="reloading" onReload={this.handleReload} />;
    }

    return (
      <ErrorScreen
        variant={isStaleAsset ? "stale" : "runtime"}
        error={error}
        onReload={this.handleReload}
        onGoHome={this.handleGoHome}
      />
    );
  }
}

type Variant = "reloading" | "stale" | "runtime";

interface ErrorScreenProps {
  variant: Variant;
  error?: Error;
  onReload: () => void;
  onGoHome?: () => void;
}

function ErrorScreen({ variant, error, onReload, onGoHome }: ErrorScreenProps) {
  const copy = COPY[variant];
  const showDetails = variant === "runtime" && error && import.meta.env.DEV;

  return (
    <section className="error-shell" role="alert" aria-live="assertive">
      <Starfield />
      <div className="error-aurora" aria-hidden="true" />
      <div className="error-vignette" aria-hidden="true" />

      <div className="error-inner">
        <div className="error-eyebrow">
          <span className="rule" />
          <span>{copy.eyebrow}</span>
          <span className="rule" />
        </div>

        <h1 className="error-title">{copy.title}</h1>
        <p className="error-lede">{copy.lede}</p>

        {variant === "reloading" ? (
          <div className="error-spinner" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        ) : (
          <div className="error-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onReload}
            >
              {copy.primary}
              <span className="arr">→</span>
            </button>
            {onGoHome && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onGoHome}
              >
                Return home
              </button>
            )}
          </div>
        )}

        {showDetails && (
          <details className="error-details">
            <summary>Technical details</summary>
            <pre>
              {error?.name}: {error?.message}
              {"\n\n"}
              {error?.stack}
            </pre>
          </details>
        )}

        <footer className="error-footer">
          Need a hand? Email{" "}
          <a href="mailto:hello@togetherinheaven.org">
            hello@togetherinheaven.org
          </a>
        </footer>
      </div>
    </section>
  );
}

const COPY: Record<Variant, {
  eyebrow: string;
  title: string;
  lede: string;
  primary: string;
}> = {
  reloading: {
    eyebrow: "Refreshing",
    title: "Updating to the latest version…",
    lede: "We've just shipped an update. Give us a breath while we pull in the new files.",
    primary: "Reload now",
  },
  stale: {
    eyebrow: "New version ready",
    title: "A fresh version of the site is live.",
    lede: "Your browser is holding onto an older copy. Reload to pick up the latest experience.",
    primary: "Reload the site",
  },
  runtime: {
    eyebrow: "Something went sideways",
    title: "We hit an unexpected note.",
    lede: "The page ran into an error we didn't plan for. Reloading usually sorts it; if it keeps happening, let us know.",
    primary: "Try again",
  },
};
